import { API_ROOT, MEDIA_ROOT } from "@/globals";
import { postRequest } from "./fetch";
import { promisifyXhr } from "./promise";

export type TUploadProgressHandler = (
  progress: number,
  loaded: number,
  total: number
) => void;

export interface IXhrResult<T> {
  status: number;
  response: T;
  isAbort: boolean;
}

export interface IMediaUpload<T> {
  abort: () => void;
  send: (
    onProgress?: TUploadProgressHandler,
    stallTimeoutMs?: number
  ) => Promise<IXhrResult<T>>;
}

interface IUploadUrlResponse {
  upload: { key: string; url: string };
}

// A 5xx on the default media complete leaves the original in storage, so
// repeating the call with the same key is safe and needs no re-upload. A
// custom completeUrl (e.g. creating a feed item) may not be idempotent, so
// it is attempted once.
const COMPLETE_MAX_ATTEMPTS = 3;

// A 5xx from the storage PUT is the storage's own transient failure. The
// pre-signed URL stays valid for an hour, so the same file can be sent again
// to it, after a short growing pause.
const STORAGE_MAX_ATTEMPTS = 3;
const STORAGE_RETRY_DELAY_MS = 1000;

// Same plain-object shape promisifyXhr rejects with, which every uploader's
// error handler already destructures.
function rejection(
  status: number,
  response: Record<string, any>,
  isAbort: boolean
) {
  return { status, response, isAbort };
}

function isRejection(error: unknown): error is ReturnType<typeof rejection> {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAbort" in error &&
    "response" in error
  );
}

function toRejection(error: unknown) {
  if (isRejection(error)) {
    return error;
  }
  const body = typeof error === "object" && error ? error : {};
  const status =
    "status" in body && typeof body.status === "number" ? body.status : 0;
  // A real Error (network failure, non-JSON response) carries no API message
  // worth showing, so callers fall back to their generic upload-error text.
  return rejection(status, error instanceof Error ? {} : body, false);
}

/**
 * Uploads through a pre-signed storage URL: the API signs, the browser sends
 * the raw bytes straight to storage, then the API is told to process them.
 * Only the storage PUT is an XHR, because `fetch()` still can't report upload
 * progress; presign and complete are plain fetch calls.
 */
export function createDirectMediaUpload<T>(
  file: Blob,
  {
    md5,
    type,
    completeUrl: customCompleteUrl,
  }: { md5: string; type: string; completeUrl?: string }
): IMediaUpload<T> {
  const completeUrl =
    customCompleteUrl ?? `${MEDIA_ROOT}/v1/media?md5=${md5}&type=${type}`;
  const completeMaxAttempts =
    customCompleteUrl === undefined ? COMPLETE_MAX_ATTEMPTS : 1;
  const abortController = new AbortController();
  let storageXhr: XMLHttpRequest | undefined;
  // Where a failure happened, which upload it was and which try of that
  // step, so the Slack reports can locate the file.
  let step: "presign" | "storage" | "complete" = "presign";
  let key: string | undefined;
  let attempt = 1;

  const abort = () => {
    abortController.abort();
    storageXhr?.abort();
  };

  const waitUnlessAborted = (ms: number) =>
    new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer);
        abortController.signal.removeEventListener("abort", done);
        resolve();
      };
      const timer = setTimeout(done, ms);
      abortController.signal.addEventListener("abort", done);
    });

  const send: IMediaUpload<T>["send"] = async (onProgress, stallTimeoutMs) => {
    // Only the storage PUT gets the stall timeout: it reports progress, so
    // silence there means a dead connection. The API calls have no progress
    // and complete legitimately takes as long as the server needs to convert
    // the file, so they wait for the server's answer.
    const post = async <R>(url: string, body: Record<string, any>) => {
      const requestController = new AbortController();
      const forwardAbort = () => {
        requestController.abort();
      };
      abortController.signal.addEventListener("abort", forwardAbort);
      try {
        return await postRequest<R>(url, body, {
          abortController: requestController,
        });
      } finally {
        abortController.signal.removeEventListener("abort", forwardAbort);
      }
    };

    try {
      const { upload } = await post<IUploadUrlResponse>(
        `${API_ROOT}/v1/file/upload-url`,
        {}
      );
      if (abortController.signal.aborted) {
        throw rejection(0, {}, true);
      }

      key = upload.key;
      step = "storage";
      for (attempt = 1; ; attempt++) {
        const xhr = new XMLHttpRequest();
        storageXhr = xhr;
        xhr.open("PUT", upload.url);
        const stored = promisifyXhr(xhr, onProgress, stallTimeoutMs);
        xhr.send(file);
        try {
          await stored;
          break;
        } catch (error) {
          if (
            abortController.signal.aborted ||
            attempt >= STORAGE_MAX_ATTEMPTS ||
            toRejection(error).status < 500
          ) {
            throw error;
          }
        }
        await waitUnlessAborted(STORAGE_RETRY_DELAY_MS * attempt);
        if (abortController.signal.aborted) {
          throw rejection(0, {}, true);
        }
      }

      step = "complete";
      for (attempt = 1; ; attempt++) {
        try {
          const response = await post<T>(completeUrl, { key: upload.key });
          return { status: 200, response, isAbort: false };
        } catch (error) {
          if (
            abortController.signal.aborted ||
            attempt >= completeMaxAttempts ||
            toRejection(error).status < 500
          ) {
            throw error;
          }
        }
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        throw rejection(0, {}, true);
      }
      const failure = toRejection(error);
      throw { ...failure, step, attempt, key };
    }
  };

  return { abort, send };
}

/** Adapts an already-configured XHR (e.g. the third-party Cloudinary target) to the same shape. */
export function wrapXhrUpload<T>(
  xhr: XMLHttpRequest,
  body: FormData
): IMediaUpload<T> {
  return {
    abort: () => {
      xhr.abort();
    },
    send: (onProgress, stallTimeoutMs) => {
      const result = promisifyXhr(xhr, onProgress, stallTimeoutMs) as Promise<
        IXhrResult<T>
      >;
      xhr.send(body);
      return result;
    },
  };
}
